import { ClipboardEvent, KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import {
  ARROW_DOWN_KEY,
  ARROW_UP_KEY,
  BACKSPACE_KEY,
  DELETE_KEY,
  ENTER_KEY,
  ESCAPE_KEY,
  F2_KEY,
  KEY_C,
  KEY_V,
} from 'common/constants/general';

interface InlineNoteEditorProps {
  value?: string | null;
  onSave: (note: string) => Promise<void> | void;
  isLoading?: boolean;
  onActiveStateChange?: (isActive: boolean) => void;
}

const normalizeNoteValue = (value?: string | null): string => value || '';
const NOTE_SELECTION_EVENT = 'inline-note-editor-selection-change';
const NOTE_MULTI_PASTE_EVENT = 'inline-note-editor-multi-paste';
const selectedEditorIds = new Set<string>();

const emitSelectionChange = () => {
  globalThis.dispatchEvent(
    new CustomEvent<string[]>(NOTE_SELECTION_EVENT, {
      detail: Array.from(selectedEditorIds),
    })
  );
};

const navigateByArrow = (event: KeyboardEvent<HTMLDivElement>, shiftKey: boolean) => {
  const tbody = event.currentTarget.closest('tbody');
  const editors = tbody
    ? Array.from(tbody.querySelectorAll<HTMLElement>('[data-inline-note-editor="true"][tabindex="0"]'))
    : [];
  const idx = editors.indexOf(event.currentTarget);
  const next = editors[event.key === ARROW_DOWN_KEY ? idx + 1 : idx - 1];
  if (!next) return;

  if (shiftKey) {
    const currentId = event.currentTarget.dataset.editorId;
    const nextId = next.dataset.editorId;
    if (currentId) selectedEditorIds.add(currentId);
    if (nextId) selectedEditorIds.add(nextId);
  } else {
    selectedEditorIds.clear();
  }
  emitSelectionChange();
  next.focus();
};

const InlineNoteEditor = ({
  value,
  onSave,
  isLoading = false,
  onActiveStateChange,
}: InlineNoteEditorProps) => {
  const editorIdRef = useRef(useId());
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const displayBoxRef = useRef<HTMLDivElement | null>(null);
  const skipBlurSaveRef = useRef(false);
  const isSavingRef = useRef(false);
  // Tracks whether focus should be restored to the display box after editing ends
  const refocusAfterEditRef = useRef(false);
  const wasEditingRef = useRef(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(normalizeNoteValue(value));
  const [placeCursorAtEnd, setPlaceCursorAtEnd] = useState(false);

  const normalizedValue = useMemo(() => normalizeNoteValue(value), [value]);

  useEffect(() => {
    if (!isEditing) {
      setDraftValue(normalizedValue);
    }
  }, [isEditing, normalizedValue]);

  useEffect(() => {
    if (onActiveStateChange) {
      onActiveStateChange(isSelected || isEditing);
    }
  }, [isSelected, isEditing, onActiveStateChange]);

  useEffect(() => {
    const handleExternalSelection = (event: Event) => {
      const customEvent = event as CustomEvent<string[]>;
      const selectedIds = customEvent.detail || [];
      setIsSelected(selectedIds.includes(editorIdRef.current));
    };

    globalThis.addEventListener(NOTE_SELECTION_EVENT, handleExternalSelection);
    return () => {
      globalThis.removeEventListener(NOTE_SELECTION_EVENT, handleExternalSelection);
    };
  }, []);

  useEffect(() => {
    const handleOutsidePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-inline-note-editor="true"]')) {
        return;
      }

      if (selectedEditorIds.size > 0) {
        selectedEditorIds.clear();
        emitSelectionChange();
      }
    };

    document.addEventListener('mousedown', handleOutsidePointerDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown);
    };
  }, []);

  useEffect(() => {
    const handleMultiPaste = async (event: Event) => {
      const customEvent = event as CustomEvent<{ sourceId: string; text: string; targetIds: string[] }>;
      const payload = customEvent.detail;
      if (!payload || payload.sourceId === editorIdRef.current || isEditing || isLoading) {
        return;
      }
      if (payload.targetIds.includes(editorIdRef.current)) {
        await onSave(payload.text);
      }
    };

    globalThis.addEventListener(NOTE_MULTI_PASTE_EVENT, handleMultiPaste);
    return () => {
      globalThis.removeEventListener(NOTE_MULTI_PASTE_EVENT, handleMultiPaste);
    };
  }, [isEditing, isLoading, onSave]);

  useEffect(() => {
    const editorId = editorIdRef.current;
    return () => {
      if (selectedEditorIds.delete(editorId)) {
        emitSelectionChange();
      }
    };
  }, []);

  const replaceSelection = () => {
    selectedEditorIds.clear();
    selectedEditorIds.add(editorIdRef.current);
    emitSelectionChange();
  };

  const toggleSelection = () => {
    if (selectedEditorIds.has(editorIdRef.current)) {
      selectedEditorIds.delete(editorIdRef.current);
    } else {
      selectedEditorIds.add(editorIdRef.current);
    }
    emitSelectionChange();
  };

  // Restore focus to the display box after editing ends (Escape or Enter), but not on blur-to-save
  useEffect(() => {
    if (wasEditingRef.current && !isEditing && refocusAfterEditRef.current) {
      refocusAfterEditRef.current = false;
      displayBoxRef.current?.focus();
    }
    wasEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    if (isEditing && placeCursorAtEnd && textAreaRef.current) {
      const end = textAreaRef.current.value.length;
      textAreaRef.current.selectionStart = end;
      textAreaRef.current.selectionEnd = end;
      setPlaceCursorAtEnd(false);
    }
  }, [isEditing, placeCursorAtEnd]);

  const startEditing = (initialValue: string, shouldPlaceCursorAtEnd: boolean) => {
    if (isLoading) {
      return;
    }
    replaceSelection();
    setDraftValue(initialValue);
    setPlaceCursorAtEnd(shouldPlaceCursorAtEnd);
    setIsSelected(true);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    refocusAfterEditRef.current = true;
    setDraftValue(normalizedValue);
    setIsEditing(false);
  };

  const saveAndClose = async (keepSelected: boolean) => {
    if (isLoading || isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    try {
      if (draftValue !== normalizedValue) {
        await onSave(draftValue);
      }
      // keepSelected = true means Enter was pressed; restore focus so arrow nav continues
      refocusAfterEditRef.current = keepSelected;
      setIsEditing(false);
      setIsSelected(keepSelected);
    } finally {
      isSavingRef.current = false;
    }
  };

  // Dispatch paste to all currently selected editors, then save to this one.
  // Works whether this cell is the only focused cell or part of a multi-selection.
  const applyPaste = async (pastedText: string) => {
    if (isEditing || isLoading || !pastedText) {
      return;
    }

    const targetIds = Array.from(selectedEditorIds);
    if (!targetIds.includes(editorIdRef.current)) {
      targetIds.push(editorIdRef.current);
    }

    globalThis.dispatchEvent(
      new CustomEvent(NOTE_MULTI_PASTE_EVENT, {
        detail: { sourceId: editorIdRef.current, text: pastedText, targetIds },
      })
    );

    await onSave(pastedText);
  };

  const handleEditingKeyDown = async (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ENTER_KEY && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      skipBlurSaveRef.current = true;
      await saveAndClose(true);
      return;
    }

    if (event.key === ESCAPE_KEY) {
      event.preventDefault();
      event.stopPropagation();
      cancelEditing();
    }
  };

  const handleClipboardOrInputKey = async (event: KeyboardEvent<HTMLDivElement>): Promise<boolean> => {
    const isMod = event.metaKey || event.ctrlKey;
    if (isMod && event.key.toLowerCase() === KEY_C) {
      event.preventDefault();
      await navigator.clipboard.writeText(normalizedValue);
      return true;
    }
    if (isMod && event.key.toLowerCase() === KEY_V) {
      event.preventDefault();
      await applyPaste(await navigator.clipboard.readText());
      return true;
    }
    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      startEditing(event.key, true);
      return true;
    }
    if (event.key === BACKSPACE_KEY || event.key === DELETE_KEY) {
      event.preventDefault();
      startEditing('', true);
      return true;
    }
    return false;
  };

  const handleSelectedKeyDown = async (event: KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (event.key === ARROW_DOWN_KEY || event.key === ARROW_UP_KEY) {
      event.preventDefault();
      navigateByArrow(event, event.shiftKey);
      return;
    }
    if (event.key === F2_KEY) {
      event.preventDefault();
      startEditing(normalizedValue, true);
      return;
    }
    await handleClipboardOrInputKey(event);
  };

  const handlePaste = async (event: ClipboardEvent<HTMLDivElement>) => {
    if (isEditing || isLoading) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    await applyPaste(event.clipboardData.getData('text'));
  };

  const hasValue = normalizedValue.trim() !== '';
  const displayText = hasValue ? normalizedValue : '-';
  const collapsedDisplayText = displayText.replaceAll('\n', ' ');
  const shouldShowTitle = displayText.includes('\n');
  const rowsCount = draftValue.includes('\n') ? 2 : 1;

  if (isEditing) {
    return (
      <Box
        data-inline-note-editor="true"
        sx={{
          width: '100%',
          height: '100%',
          minHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          boxSizing: 'border-box',
        }}>
        <TextField
          autoFocus
          multiline
          fullWidth
          minRows={rowsCount}
          maxRows={10}
          value={draftValue}
          onChange={event => setDraftValue(event.target.value)}
          onKeyDown={handleEditingKeyDown}
          placeholder="Add note"
          variant="standard"
          size="small"
          disabled={isLoading}
          onClick={event => event.stopPropagation()}
          onBlur={async () => {
            if (skipBlurSaveRef.current) {
              skipBlurSaveRef.current = false;
              return;
            }
            await saveAndClose(false);
          }}
          inputRef={textAreaRef}
          slotProps={{
            input: { disableUnderline: true },
          }}
          sx={{
            width: '100%',
            '& .MuiInputBase-root': {
              fontSize: '0.875rem',
              lineHeight: 1.43,
              padding: 0,
            },
            '& .MuiInputBase-inputMultiline': {
              padding: 0,
              overflowY: 'auto',
            },
          }}
        />
      </Box>
    );
  }

  const text = (
    <Typography
      variant="body2"
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        color: hasValue ? 'text.primary' : 'text.secondary',
        fontStyle: hasValue ? 'normal' : 'italic',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
      {collapsedDisplayText}
    </Typography>
  );

  const activeCursor = isSelected || isEditing ? 'text' : 'pointer';
  const cursorStyle = isLoading ? 'default' : activeCursor;

  return (
    <Box
      ref={displayBoxRef}
      data-inline-note-editor="true"
      data-editor-id={editorIdRef.current}
      tabIndex={0}
      onFocus={() => {
        if (selectedEditorIds.size === 0) {
          replaceSelection();
        } else {
          setIsSelected(selectedEditorIds.has(editorIdRef.current));
        }
      }}
      onBlur={() => {
        // Don't deselect if this cell is still part of a multi-selection
        if (!selectedEditorIds.has(editorIdRef.current)) {
          setIsSelected(false);
        }
      }}
      onKeyDown={handleSelectedKeyDown}
      onPaste={event => handlePaste(event)}
      onClick={event => {
        event.stopPropagation();
        event.currentTarget.focus();
        if (event.metaKey || event.ctrlKey || event.shiftKey) {
          toggleSelection();
        } else {
          replaceSelection();
          setIsSelected(true);
        }
      }}
      onDoubleClick={event => {
        event.stopPropagation();
        replaceSelection();
        startEditing(normalizedValue, true);
      }}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1,
        boxSizing: 'border-box',
        overflow: 'hidden',
        outline: 'none',
        cursor: cursorStyle,
      }}>
      {shouldShowTitle ? (
        <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }} title={displayText}>
          {text}
        </Box>
      ) : (
        text
      )}
    </Box>
  );
};

export default InlineNoteEditor;
